import { AnswerByQuestion } from '../service/gen';
import { Question, QuestionInput, TemplateQuestionType } from '../service/gen/templates';
import {
    BaseFormQuestion,
    ChoiceAnswer,
    ChoiceAppearance,
    ChoiceQuestion,
    DateTimeAnswer,
    DateTimeQuestion,
    FormEntry,
    FormQuestion,
    FormQuestionType,
    TextAnswer,
    TextNumberRestriction,
    TextNumberRestrictionType,
    TextQuestion,
} from './implementations';
import { isDefined, parseFloatStrictly } from './shims';

/**
 * The question info for text question, which is designed to keep compatibility with MSForms.
 *
 * @property Multiline Whether the text question is multiline.
 * @property IsNumber Whether the text question is a number question.
 * @property NumberValidationRule The number validation rule for the text question.
 * @property NumberMinBoundary The minimum boundary for the number validation rule.
 * @property NumberMaxBoundary The maximum boundary for the number validation rule.
 */
interface TextQuestionInfo {
    Multiline?: boolean;
    IsNumber?: boolean;
    NumberValidationRule?:
        | 'Greater'
        | 'GreaterOrEqual'
        | 'Less'
        | 'LessOrEqual'
        | 'Equal'
        | 'NotEqual'
        | 'Between'
        | 'NotBetween';
    NumberMinBoundary?: string;
    NumberMaxBoundary?: string;
}

/**
 * The question info for choice question, which is designed to keep compatibility with MSForms.
 *
 * @property Choices The choices for the choice question.
 * @property Multiple Whether the choice question allows multiple choices.
 * @property OptionDisplayStyle The option display style for the choice question.
 * @property ShuffleOptions Whether the choice question shuffles options.
 * @property AllowOtherAnswer Whether the choice question allows other answers.
 */
interface ChoiceQuestionInfo {
    Choices?: { Description?: string }[];
    Multiple?: boolean;
    OptionDisplayStyle?: 'ListAll' | 'Dropdown';
    ShuffleOptions?: boolean;
    AllowOtherAnswer?: boolean;
}

/**
 * The default question info, which is designed to keep compatibility with MSForms. It is an empty object.
 */
type DefaultQuestionInfo = Record<never, unknown>;

type AnyQuestionInfo = TextQuestionInfo | ChoiceQuestionInfo | DefaultQuestionInfo;

/**
 * Maps template question type to form question type.
 *
 * @param type The template question type.
 * @returns The form question type.
 */
function mapTemplateQuestionTypeToFormQuestionType(type: TemplateQuestionType): FormQuestionType {
    switch (type) {
        case TemplateQuestionType.TextField_Single:
        case TemplateQuestionType.TextField_Multiline:
        case TemplateQuestionType.TextField_Number:
            return FormQuestionType.Text;
        case TemplateQuestionType.Choice_SingleRadio:
        case TemplateQuestionType.Choice_SingleDropdown:
        case TemplateQuestionType.Choice_MultiCheckbox:
            return FormQuestionType.Choice;
        case TemplateQuestionType.DateTime_Default:
            return FormQuestionType.DateTime;
    }
}

/**
 * Maps restriction to text number restriction.
 *
 * @param restriction The restriction.
 * @returns The text number restriction if the restriction is valid. Otherwise, return undefined.
 */
function mapRestrictionToTextNumberRestriction(
    restriction?: Pick<TextQuestionInfo, 'NumberValidationRule' | 'NumberMinBoundary' | 'NumberMaxBoundary'>
): TextNumberRestriction | undefined {
    if (!restriction) return;

    const min = parseFloatStrictly(restriction.NumberMinBoundary ?? '');
    const max = parseFloatStrictly(restriction.NumberMaxBoundary ?? '');
    switch (restriction.NumberValidationRule) {
        case 'Greater':
            if (isNaN(min)) return;
            return { type: TextNumberRestrictionType.GreaterThan, min };
        case 'GreaterOrEqual':
            if (isNaN(min)) return;
            return { type: TextNumberRestrictionType.GreaterThanOrEqualTo, min };
        case 'Less':
            if (isNaN(max)) return;
            return { type: TextNumberRestrictionType.LessThan, max };
        case 'LessOrEqual':
            if (isNaN(max)) return;
            return { type: TextNumberRestrictionType.LessThanOrEqualTo, max };
        case 'Equal':
            if (isNaN(min)) return;
            return { type: TextNumberRestrictionType.EqualTo, value: min };
        case 'NotEqual':
            if (isNaN(min)) return;
            return { type: TextNumberRestrictionType.NotEqualTo, value: min };
        case 'Between':
            if (isNaN(min) || isNaN(max)) return;
            return { type: TextNumberRestrictionType.Between, min, max };
        case 'NotBetween':
            if (isNaN(min) || isNaN(max)) return;
            return { type: TextNumberRestrictionType.NotBetween, min, max };
    }
}

/**
 * Maps question to form question.
 *
 * @param question The question.
 * @returns The form question if the question is valid. Otherwise, return undefined.
 */
function mapQuestionToFormQuestion(question: Question): FormQuestion | undefined {
    const type = mapTemplateQuestionTypeToFormQuestionType(question.QuestionType);
    if (type === undefined) return;

    const base: BaseFormQuestion<FormQuestionType> = {
        type,
        id: question.Id,
        order: question.Order,
        title: question.Title,
        rtTitle: question.RTQuestionTitle,
        subtitle: question.Subtitle,
        rtSubtitle: question.RTQuestionSubtitle,
        required: question.Required,
        tooltip: question.Tooltip,
        placeholder: question.Placeholder,
    };

    switch (base.type) {
        case FormQuestionType.Text:
            const textQuestionInfo = safeParseJson(question.QuestionInfo) as TextQuestionInfo | undefined;
            const textQuestion: TextQuestion = {
                ...(base as BaseFormQuestion<FormQuestionType.Text>),
                multiline: question.QuestionType === TemplateQuestionType.TextField_Multiline,
                number: question.QuestionType === TemplateQuestionType.TextField_Number,
                restriction: mapRestrictionToTextNumberRestriction(textQuestionInfo),
            };
            return textQuestion;
        case FormQuestionType.Choice:
            const choiceQuestionInfo = safeParseJson(question.QuestionInfo) as ChoiceQuestionInfo | undefined;
            const choiceQuestion: ChoiceQuestion = {
                ...(base as BaseFormQuestion<FormQuestionType.Choice>),
                appearance:
                    question.QuestionType === TemplateQuestionType.Choice_SingleRadio
                        ? ChoiceAppearance.Radio
                        : question.QuestionType === TemplateQuestionType.Choice_SingleDropdown
                        ? ChoiceAppearance.Dropdown
                        : ChoiceAppearance.Checkbox,
                choices: normalizeStringSet(choiceQuestionInfo?.Choices?.map(choice => choice.Description) ?? []),
                shuffle: choiceQuestionInfo?.ShuffleOptions,
                allowOther: choiceQuestionInfo?.AllowOtherAnswer,
            };
            return choiceQuestion;
        case FormQuestionType.DateTime:
            const dateTimeQuestion: DateTimeQuestion = base as BaseFormQuestion<FormQuestionType.DateTime>;
            return dateTimeQuestion;
    }
}

/**
 * Maps a question to a form entry.
 *
 * @param question The question.
 * @returns The form entry.
 */
export function mapQuestionToFormEntry(question: Question): FormEntry | undefined {
    const formQuestion = mapQuestionToFormQuestion(question);
    if (!formQuestion) return;

    return { question: formQuestion, answer: {} } as FormEntry;
}

/**
 * Maps form question type to template question type.
 *
 * @param question The form question.
 * @returns The template question type.
 */
function mapFormQuestionToTemplateQuestionType(question: FormQuestion): TemplateQuestionType {
    switch (question.type) {
        case FormQuestionType.Text:
            return question.number
                ? TemplateQuestionType.TextField_Number
                : question.multiline
                ? TemplateQuestionType.TextField_Multiline
                : TemplateQuestionType.TextField_Single;
        case FormQuestionType.Choice:
            switch (question.appearance) {
                case ChoiceAppearance.Radio:
                    return TemplateQuestionType.Choice_SingleRadio;
                case ChoiceAppearance.Dropdown:
                    return TemplateQuestionType.Choice_SingleDropdown;
                case ChoiceAppearance.Checkbox:
                    return TemplateQuestionType.Choice_MultiCheckbox;
            }
        case FormQuestionType.DateTime:
            return TemplateQuestionType.DateTime_Default;
    }
}

/**
 * Maps text number restriction to restriction.
 *
 * @param restriction The text number restriction.
 * @returns The restriction if the text number restriction is valid. Otherwise, return undefined.
 */
function mapTextNumberRestrictionToRestriction(
    restriction?: TextNumberRestriction
): Pick<TextQuestionInfo, 'NumberValidationRule' | 'NumberMinBoundary' | 'NumberMaxBoundary'> | undefined {
    if (!restriction) return;

    switch (restriction.type) {
        case TextNumberRestrictionType.GreaterThan:
            return {
                NumberValidationRule: 'Greater',
                NumberMinBoundary: restriction.min.toString(),
            };
        case TextNumberRestrictionType.GreaterThanOrEqualTo:
            return {
                NumberValidationRule: 'GreaterOrEqual',
                NumberMinBoundary: restriction.min.toString(),
            };
        case TextNumberRestrictionType.LessThan:
            return {
                NumberValidationRule: 'Less',
                NumberMaxBoundary: restriction.max.toString(),
            };
        case TextNumberRestrictionType.LessThanOrEqualTo:
            return {
                NumberValidationRule: 'LessOrEqual',
                NumberMaxBoundary: restriction.max.toString(),
            };
        case TextNumberRestrictionType.EqualTo:
            return {
                NumberValidationRule: 'Equal',
                NumberMinBoundary: restriction.value.toString(),
            };
        case TextNumberRestrictionType.NotEqualTo:
            return {
                NumberValidationRule: 'NotEqual',
                NumberMinBoundary: restriction.value.toString(),
            };
        case TextNumberRestrictionType.Between:
            return {
                NumberValidationRule: 'Between',
                NumberMinBoundary: restriction.min.toString(),
                NumberMaxBoundary: restriction.max.toString(),
            };
        case TextNumberRestrictionType.NotBetween:
            return {
                NumberValidationRule: 'NotBetween',
                NumberMinBoundary: restriction.min.toString(),
                NumberMaxBoundary: restriction.max.toString(),
            };
    }
}

/**
 * Gets question info from form question.
 *
 * @param question The form question.
 * @returns The question info.
 */
function getQuestionInfoFromFormQuestion(question: FormQuestion): AnyQuestionInfo {
    switch (question.type) {
        case FormQuestionType.Text:
            return mapTextNumberRestrictionToRestriction(question.restriction) ?? {};
        case FormQuestionType.Choice:
            return {
                Choices: question.choices.map(choice => ({ Description: choice })),
                ShuffleOptions: question.shuffle ?? false,
                AllowOtherAnswer: question.allowOther ?? false,
            };
        default:
            return {};
    }
}

/**
 * Maps form question to question input.
 *
 * @param formQuestion The form question.
 * @returns The question input.
 */
function mapFormQuestionToQuestionInput(formQuestion: FormQuestion): QuestionInput {
    return {
        Title: formQuestion.title,
        Subtitle: formQuestion.subtitle,
        Required: formQuestion.required ?? false,
        QuestionType: mapFormQuestionToTemplateQuestionType(formQuestion),
        Order: formQuestion.order,
        QuestionInfo: JSON.stringify(getQuestionInfoFromFormQuestion(formQuestion)),
        Tooltip: formQuestion.tooltip,
        Placeholder: formQuestion.placeholder,
        RTQuestionTitle: formQuestion.rtTitle,
        RTQuestionSubtitle: formQuestion.rtSubtitle,
    };
}

/**
 * Maps form entries to question inputs.
 *
 * @param formEntries The form entries.
 * @returns The question inputs.
 */
export function mapFormEntriesToQuestionInputs(formEntries: FormEntry[]): QuestionInput[] {
    return formEntries.map(formEntry => mapFormQuestionToQuestionInput(formEntry.question));
}

/**
 * Maps form question to question.
 *
 * @param formQuestion The form question.
 * @returns The question.
 */
function mapFormQuestionToQuestion(formQuestion: FormQuestion): Question {
    return {
        Id: formQuestion.id,
        ...mapFormQuestionToQuestionInput(formQuestion),
    };
}

/**
 * Maps form entries to questions.
 *
 * @param formEntries The form entries.
 * @returns The questions.
 */
export function mapFormEntriesToQuestions(formEntries: FormEntry[]): Question[] {
    return formEntries.map(({ question }) => mapFormQuestionToQuestion(question));
}

/**
 * Safely parse json as object.
 *
 * @param json The json string to parse.
 * @returns The parsed object if the json string is valid. Otherwise, return undefined.
 */
function safeParseJson(json?: string): unknown {
    if (!json) return;
    try {
        return JSON.parse(json);
    } catch (e) {
        return;
    }
}

/**
 * Normalize string set.
 * Remove non-string elements, empty strings, and duplicates.
 *
 * @param array The string set array to normalize.
 * @returns The normalized string set array.
 *
 * @example normalizeStringSet(['', 1, true, undefined, null, 'Another', 'Another', 'Yet']) => ['Another', 'Yet']
 */
function normalizeStringSet(array: unknown[]): string[] {
    return Array.from(new Set(array.filter((o): o is string => Boolean(typeof o === 'string' && o))));
}

/**
 * Format date as local date string. (yyyy-MM-dd)
 *
 * @param date The date to format.
 * @returns The formatted date string.
 */
function formatLocalDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return [year, month, day].join('-');
}

/**
 * Maps form entry to answer by question.
 * If the question id is undefined or of non-supported type, it will return undefined.
 * If the answer is undefined or without values, it will return an answer input with only the question id.
 * Otherwise, it will return an answer input with the question id and the stringified answer content.
 *
 * @param entry The form entry.
 * @returns The answer by question.
 */
export function mapFormEntryToAnswerByQuestion({ question, answer }: FormEntry): AnswerByQuestion | undefined {
    if (!question.id) return;

    const input = { QuestionId: question.id };
    switch (question.type) {
        case FormQuestionType.Text:
            const textAnswer = answer as TextAnswer;
            if (!textAnswer.content) return input;
            return { ...input, AnswerContent: textAnswer.content };
        case FormQuestionType.Choice:
            const choices = question.choices;
            const choiceAnswer = answer as ChoiceAnswer;
            if (!choiceAnswer.content) return input;
            const { chosen, other } = choiceAnswer.content;
            const indexLookup = new Map(choices.map((choice, index) => [choice, index]));
            const chosenAndOther = normalizeStringSet(other ? [...chosen, other] : chosen).sort(
                (a, b) => (indexLookup.get(a) ?? choices.length) - (indexLookup.get(b) ?? choices.length)
            );
            const multiple = question.appearance === ChoiceAppearance.Checkbox;
            return {
                ...input,
                AnswerContent: multiple ? JSON.stringify(chosenAndOther) : chosenAndOther[0],
            };
        case FormQuestionType.DateTime:
            const dateTimeAnswer = answer as DateTimeAnswer;
            if (!dateTimeAnswer.content) return input;
            return { ...input, AnswerContent: formatLocalDateString(dateTimeAnswer.content) };
    }
}

interface FormTemplate {
    questions?: {
        type?: 'Question.TextField' | 'Question.Choice' | 'Question.DateTime';
        id?: string;
        order?: number;
        title?: string;
        required?: boolean;
        questionInfo?: string;
    }[];
}

/**
 * Parses form template as form entries.
 *
 * @param formTemplate The form template to parse.
 * @returns The parsed form template as form entries.
 */
export function parseFormTemplate(formTemplate: string): FormEntry[] {
    const parsed = safeParseJson(formTemplate);
    if (!parsed) return [];

    const questions = (parsed as FormTemplate).questions;
    if (!questions) return [];

    return questions
        .map(({ type, id, order, title, required, questionInfo }) => {
            if (order === undefined || id === undefined || !questionInfo) return;
            const info = safeParseJson(questionInfo) as AnyQuestionInfo;
            const parsedType =
                type === 'Question.TextField'
                    ? FormQuestionType.Text
                    : type === 'Question.Choice'
                    ? FormQuestionType.Choice
                    : type === 'Question.DateTime'
                    ? FormQuestionType.DateTime
                    : undefined;

            if (parsedType === undefined) return;
            const base: BaseFormQuestion<FormQuestionType> = { type: parsedType, id, order, title, required };

            switch (base.type) {
                case FormQuestionType.Text:
                    const textQuestionInfo = info as TextQuestionInfo;
                    const textQuestion: TextQuestion = {
                        ...(base as BaseFormQuestion<FormQuestionType.Text>),
                        multiline: textQuestionInfo.Multiline,
                        number: textQuestionInfo.IsNumber,
                        restriction: mapRestrictionToTextNumberRestriction(textQuestionInfo),
                    };
                    return textQuestion;
                case FormQuestionType.Choice:
                    const choiceQuestionInfo = info as ChoiceQuestionInfo;
                    const choiceQuestion: ChoiceQuestion = {
                        ...(base as BaseFormQuestion<FormQuestionType.Choice>),
                        appearance: choiceQuestionInfo.Multiple
                            ? ChoiceAppearance.Checkbox
                            : choiceQuestionInfo.OptionDisplayStyle === 'Dropdown'
                            ? ChoiceAppearance.Dropdown
                            : ChoiceAppearance.Radio,
                        choices: normalizeStringSet(
                            choiceQuestionInfo.Choices?.map(choice => choice.Description) ?? []
                        ),
                    };
                    return choiceQuestion;
                case FormQuestionType.DateTime:
                    const dateTimeQuestion: DateTimeQuestion = base as BaseFormQuestion<FormQuestionType.DateTime>;
                    return dateTimeQuestion;
            }
        })
        .filter(isDefined)
        .map(question => ({ question, answer: {} } as FormEntry));
}
